import React, { useState, useEffect } from 'react';
import './WatchDemoSqueeze.css';
import { analytics } from '../../lib/analytics';
import { emailService } from '../../lib/emailService';

interface WatchDemoSqueezeProps {
  isOpen: boolean;
  onClose: () => void;
}

const WatchDemoSqueeze: React.FC<WatchDemoSqueezeProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [showVideo, setShowVideo] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load email from sessionStorage on mount
  useEffect(() => {
    const storedEmail = sessionStorage.getItem('userEmail');
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);

  // Prevent body scroll when modal is open on mobile
  useEffect(() => {
    if (isOpen && isMobile) {
      // Simply prevent scroll without moving the page
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      
      return () => {
        // Restore body scroll
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
      };
    }
  }, [isOpen, isMobile]);

  // Save email to sessionStorage whenever it changes
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // If email changed and status was submitted, reset to idle to allow resubmit
    const storedEmail = sessionStorage.getItem('userEmail');
    if (submitStatus === 'submitted' && newEmail !== storedEmail) {
      setSubmitStatus('idle');
      setIsDuplicate(false);
      setErrorMessage('');
    }
    
    if (newEmail.trim()) {
      sessionStorage.setItem('userEmail', newEmail);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitStatus !== 'idle' && submitStatus !== 'error') return;
    
    // Set submitting state
    setSubmitStatus('submitting');
    setErrorMessage('');
    
    try {
      // Validate email
      if (!email || !email.includes('@')) {
        setErrorMessage('Please enter a valid email address');
        setSubmitStatus('error');
        return;
      }

      analytics.trackFormSubmit('watch_demo_form', { email });
      
      const result = await analytics.createUser(email, 'demo');
      
      if (!result.success) {
        setErrorMessage('Failed to save your email. Please try again.');
        setSubmitStatus('error');
        return;
      }
      
      if (result.isDuplicate) {
        console.log('User already in system');
        sessionStorage.setItem('userEmail', email);
        
        setIsDuplicate(true);
        setSubmitStatus('submitted');
        return;
      }
      
      setIsDuplicate(false);
      
      console.log('Demo request saved to Supabase');
      
      sessionStorage.setItem('userEmail', email);
      
      try {
        const emailSent = await emailService.sendDemoFollowup(email);
        if (emailSent) {
          console.log('Demo email sent successfully');
        } else {
          console.warn('Demo email failed to send, but user was saved');
        }
      } catch (emailError) {
        console.error('Error sending demo email:', emailError);
      }
      
      analytics.trackInteraction('demo_request_success', 'watch_demo_form');
      
      setSubmitStatus('submitted');
    } catch (error) {
      console.error('Error submitting demo request:', error);
      setErrorMessage('Something went wrong. Please try again.');
      setSubmitStatus('error');
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSubmitStatus('idle');
      setShowVideo(false);
    }
  }, [isOpen]);

  // Auto-close after 2 seconds on desktop when submission is complete
  useEffect(() => {
    if (submitStatus === 'submitted' && !isMobile) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [submitStatus, isMobile, onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };


  if (!isOpen && !showVideo) return null;

  return (
    <>
      {/* Demo Signup Popup */}
      {isOpen && !showVideo && (
        <div className="popup-overlay watch-demo" onClick={handleBackdropClick}>
          <div className="popup-container watch-demo">
            <button className="popup-close" onClick={onClose} aria-label="Close popup">
              ×
            </button>
            <div className="popup-content">
              {/* Left side - Content */}
              <div className="popup-left">
                <div className="popup-text-content">
                  <h1 className="popup-headline">
                  Watch how Archaas cuts rework and wins faster approvals
                  </h1>
                  <p className="popup-subtitle">
                  See how AI catches worldwide compliance issues, streamlines project workflows and speeds approvals – all in one smart workspace built for architects.
                  </p>
                  <ul className="popup-benefits">
                    <li>✓ Experience instant, cited answers from global regulations</li>
                    <li>✓ Watch AI run compliance checks on a live project</li>
                    <li>✓ Learn how unified practice intelligence streamlines every delivery</li>
                  </ul>
                  <p className="popup-details">
                    Get access with just your email →
                  </p>
                </div>
              </div>

              {/* Right side - Demo form */}
              <div className="popup-right">
                <button className="popup-close-mobile" onClick={onClose} aria-label="Close popup">
                  ×
                </button>
                <div className="popup-form-container">
                  <img src="/Logo Pack-30.png" alt="Archaas Logo" className="popup-logo" />
                  <h2 className="popup-form-title">Watch Free Demo</h2>
                  <p className="popup-form-subtitle">
                    See exactly how our tool works in real architectural projects.
                  </p>
                  
                  <form className="popup-form" onSubmit={handleSubmit}>
                    <div className="popup-input-container">
                      <div className="popup-input-row email-row">
                        <input
                          type="email"
                          placeholder="Your Email"
                          value={email}
                          onChange={handleEmailChange}
                          className="popup-input email-input"
                          required
                          disabled={submitStatus === 'submitting'}
                        />
                        <button 
                          type="submit" 
                          className={`popup-button ${submitStatus === 'submitting' ? 'popup-button--loading' : ''}`}
                          disabled={submitStatus === 'submitting'}
                        >
                          {submitStatus === 'idle' && 'Watch Demo'}
                          {submitStatus === 'error' && 'Try Again'}
                          {submitStatus === 'submitting' && (
                            <>
                              <span className="loading-spinner"></span>
                              Joining...
                            </>
                          )}
                          {submitStatus === 'submitted' && (
                            <>
                              <span className="success-checkmark">✓</span>
                              Thank you!
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    {submitStatus === 'submitted' ? (
                      <p className="popup-success-message" style={{ color: '#10b981' }}>
                        {isDuplicate ? "You're already on our waitlist!" : "Thank you! We're finalising the demo and will send it to your email shortly."}
                      </p>
                    ) : submitStatus === 'error' ? (
                      <p className="popup-success-message" style={{ color: '#ef4444' }}>{errorMessage}</p>
                    ) : (
                      <p className="popup-privacy">
                      We will never share your information with anyone.
                      </p>
                    )}
                    
                    <div className="popup-social-proof">
                      <div className="popup-avatars">
                        <img src="/headshot2.jpg" alt="User 2" className="popup-avatar" />
                        <img src="/headshot4.jpg" alt="User 3" className="popup-avatar" />
                        <img src="/headshot3.jpg" alt="User 4" className="popup-avatar" />
                        <img src="/headshot5.jpg" alt="User 5" className="popup-avatar" />
                        <div className="hero-avatar hero-avatar-plus">+</div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Popup - Now shows demo access */}
      {/* TEMPORARILY DISABLED: Re-enable when full demo is ready */}
      {/* <VideoSqueeze 
        isOpen={showVideo}
        onClose={handleVideoClose}
        userEmail={email}
      /> */}
    </>
  );
};

export default WatchDemoSqueeze; 